'use client'

import { twMerge } from '@/lib/classname/twMerge'
import { getCountryTranslation } from '@/lib/i18n/countries'
import { useTranslation } from '@/lib/i18n/useTranslation.client'
import { useMemo } from 'react'
import { AddressCompanyNationalId } from './_components/AddressCompanyNationalId'
import type { AddressContactProps } from './_components/AddressContact'
import { AddressContact } from './_components/AddressContact'
import { AddressDistance, type AddressDistanceProps } from './_components/AddressDistance'
import type { AddressTitleProps } from './_components/AddressTitle'
import { AddressTitle } from './_components/AddressTitle'

export type AddressProps = UiComponentProps & {
  /** The first line of the address */
  line1: string
  /** The second line of the address */
  line2?: string | null
  /** The third line of the address */
  line3?: string | null
  /** The postal code of the address */
  postalCode: string
  /** The city of the address */
  city: string
  /** The ISO country code of the address (e.g. "FR") */
  isoCountryCode?: string
  /** The country of the address */
  country?: string
  /** The national ID of the company */
  companyNationalId?: string
  /** If we hide the address title */
  noTitle?: boolean
}

export function Address({
  name,
  line1,
  line2,
  line3,
  postalCode,
  city,
  isoCountryCode,
  country,
  phone,
  onPhonePress,
  email,
  companyNationalId,
  distance,
  unit,
  'data-testid': dataTestid = 'address',
  className,
  noTitle,
}: AddressProps & AddressContactProps & AddressTitleProps & AddressDistanceProps) {
  const { t } = useTranslation(['countries'])

  const translatedCountryName = useMemo(
    () => getCountryTranslation(isoCountryCode, t, country),
    [isoCountryCode, country, t],
  )

  return (
    <dl data-testid={dataTestid} className={twMerge('flex w-full flex-col gap-1', className)}>
      {(!noTitle || distance) && (
        <dt>
          <span className="inline-flex w-full">
            {!noTitle && <AddressTitle name={name} data-testid={dataTestid} />}
            <AddressDistance
              distance={distance}
              unit={unit}
              data-testid={`${dataTestid}/distance`}
              className="ml-auto"
            />
          </span>
        </dt>
      )}
      <dd>
        <address
          data-testid={`${dataTestid}/details`}
          className="flex flex-col break-all not-italic leading-light"
        >
          <span data-testid={`${dataTestid}/line-1`}>{line1}</span>
          <span data-testid={`${dataTestid}/line-2`}>{line2}</span>
          <span data-testid={`${dataTestid}/line-3`}>{line3}</span>
          <span data-testid={`${dataTestid}/city`} className="uppercase">
            {postalCode} {city}
          </span>
          <span data-testid={`${dataTestid}/country`}>{translatedCountryName}</span>
          <AddressContact
            phone={phone}
            email={email}
            onPhonePress={onPhonePress}
            data-testid={dataTestid}
          />
          <AddressCompanyNationalId
            companyNationalId={companyNationalId}
            data-testid={dataTestid}
          />
        </address>
      </dd>
    </dl>
  )
}
